'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { differenceInDays } from 'date-fns';

import AppBanner from '@/components/common/AppBanner/AppBanner';
import PriveeInvitedBanner from '@/components/common/PriveeInvitedBanner/PriveeInvitedBanner';
import AcceptInvitationModal from '@/app/reserve/AcceptInvitationModal/PriveeAcceptInvitationModal';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import { useFlags } from '@/components/hook/flags';
import { useModal } from '@/components/hook/modal';
import { FLAG_KEYS } from '@/const/flags';
import { FallbackSubscriptionPrices } from '@/types/Subscription';
import { PRIVEE_STATUS } from '@/types/User';

export const PRIVEE_INVITED_CACHE_KEY = 'priveeInvitedBannerStatus';
export const PRIVEE_INVITED_DISMISS_KEY = 'priveeInvitedBannerDismissed';

type CachedPriveeInvitedStatus = {
  invited: boolean;
  checkedAt: number;
};

const readPriveeCache = (): CachedPriveeInvitedStatus | null => {
  try {
    const raw = localStorage.getItem(PRIVEE_INVITED_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.invited !== 'boolean') return null;
    return parsed as CachedPriveeInvitedStatus;
  } catch {
    return null;
  }
};

const writePriveeCache = (invited: boolean) => {
  try {
    localStorage.setItem(
      PRIVEE_INVITED_CACHE_KEY,
      JSON.stringify({ invited, checkedAt: Date.now() })
    );
  } catch {
    // Ignore quota / private-mode errors
  }
};

const clearPriveeCache = () => {
  try {
    localStorage.removeItem(PRIVEE_INVITED_CACHE_KEY);
  } catch {
    // Ignore
  }
};

const clearPriveeDismiss = () => {
  try {
    sessionStorage.removeItem(PRIVEE_INVITED_DISMISS_KEY);
  } catch {
    // Ignore
  }
};

/** "reserve" as a whole path token: /reserve, /a/reserve/b, /perks-reserve. */
export const RESERVE_PATH = /(^|[/-])reserve([/-]|$)/;

export type BannerOrchestratorProps = {
  hiddenAppBanner?: boolean;
  onPriveeBannerVisibilityChange?: (visible: boolean) => void;
};

/**
 * BannerOrchestrator owns the show/hide rules for site-wide banners that
 * aren't CMS-driven: the iOS install AppBanner and the Réservé invited
 * banner. CMS BuilderSiteBanner stays in AppShellLayer because it depends
 * on server-fetched content; the orchestrator reports Réservé banner
 * visibility so AppShellLayer can hide BuilderSiteBanner when Réservé wins.
 */
const BannerOrchestrator = ({
  hiddenAppBanner,
  onPriveeBannerVisibilityChange
}: BannerOrchestratorProps) => {
  const { isIOSWebView, user } = useContextBroker();
  const { value: flagValues } = useFlags();
  const { showModal, closeModal, isOpen } = useModal();
  const pathname = usePathname();
  const isOnExcludedPage =
    pathname === '/login' ||
    pathname?.startsWith('/login/') ||
    // Any Réservé-themed page — the banner's "Accept my invitation" CTA opens
    // a modal, which is redundant/confusing when already on a dedicated
    // Réservé page. Matches "reserve" as a whole path token so it covers both
    // shapes: segment routes (/reserve, /collection/reserve,
    // /myaccount/manage/reserve) and the CMS slugs this PR deep-links to
    // (/how-it-works-reserve, /perks-reserve). The token boundary is what
    // keeps an unrelated /reserved-items from matching.
    RESERVE_PATH.test(pathname ?? '') ||
    // The iOS webview never wants this desktop banner+modal: every flow it
    // covers (accept, freeze, cancel, ...) has its own dedicated minipage,
    // and pages like /myaccount/closet don't contain "reserve" as a path
    // token so the check above misses them.
    isIOSWebView;

  // BannerOrchestrator lives in the root layout and never unmounts, so an
  // unmount cleanup wouldn't close the AcceptInvitationModal if the user navigates
  // away after accepting (while the modal still shows "Redirecting...").
  // Close it on any pathname change instead. `isOpen` is intentionally excluded from
  // deps — we only want this to fire on navigation, not on every modal state change.
  useEffect(() => {
    if (isOpen) closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // ── App Banner (iOS install) ───────────────────────────────────────────
  const [displayAppBanner, setDisplayAppBanner] = useState<boolean>(false);

  useEffect(() => {
    if (hiddenAppBanner) {
      setDisplayAppBanner(false);
      return;
    }

    if (!flagValues[FLAG_KEYS.APP_BANNER]) {
      setDisplayAppBanner(false);
      return;
    }

    if (isIOSWebView) {
      setDisplayAppBanner(false);
      return;
    }

    const dismissedAt = localStorage.getItem('appBannerDismissedDate') || '';
    const today = new Date().toDateString();

    if (
      (user?.status && !dismissedAt) ||
      (!!dismissedAt &&
        differenceInDays(new Date(today), new Date(dismissedAt)) >= 7)
    ) {
      setDisplayAppBanner(true);
    } else {
      setDisplayAppBanner(false);
    }
  }, [flagValues, isIOSWebView, hiddenAppBanner, user?.status]);

  const handleCloseAppBanner = () => {
    localStorage.setItem('appBannerDismissedDate', new Date().toDateString());
    setDisplayAppBanner(false);
  };

  // ── Réservé Invited Banner ──────────────────────────────────────────────
  const [shouldShowPriveeBanner, setShouldShowPriveeBanner] =
    useState<boolean>(false);
  const previousUserIdRef = useRef<string | undefined>(user?.id);

  // Notify parent so it can hide BuilderSiteBanner when Réservé banner shows
  useEffect(() => {
    onPriveeBannerVisibilityChange?.(shouldShowPriveeBanner);
  }, [shouldShowPriveeBanner, onPriveeBannerVisibilityChange]);

  // Clear Réservé storage when user logs out or switches identities
  useEffect(() => {
    const previousId = previousUserIdRef.current;
    const currentId = user?.id;
    if (previousId !== currentId) {
      clearPriveeCache();
      clearPriveeDismiss();
      previousUserIdRef.current = currentId;
    }
  }, [user?.id]);

  // Decide whether to show the Réservé invited banner
  useEffect(() => {
    let cancelled = false;

    // Don't show on Réservé-themed pages — banner CTA is redundant there.
    if (isOnExcludedPage) {
      setShouldShowPriveeBanner(false);
      return;
    }

    if (!user?.id) {
      clearPriveeCache();
      setShouldShowPriveeBanner(false);
      return;
    }

    // Clear Privee cache and hide banner.
    // The PriveeProductList on /collection/privee will take care of closing the modal after user is redirected
    if (user.priveeStatus === PRIVEE_STATUS.ACTIVE) {
      clearPriveeCache();
      setShouldShowPriveeBanner(false);
      return;
    }

    if (user.priveeStatus === PRIVEE_STATUS.CANCELLED) {
      clearPriveeCache();
      setShouldShowPriveeBanner(false);
      return;
    }

    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(PRIVEE_INVITED_DISMISS_KEY) === 'true';
    } catch {
      // Ignore
    }
    if (dismissed) {
      setShouldShowPriveeBanner(false);
      return;
    }

    // Cookie already says invited — show banner immediately, skip Upstash.
    // Cache the result so subsequent loads don't even hit this branch.
    if (user.priveeStatus === PRIVEE_STATUS.INVITED) {
      writePriveeCache(true);
      setShouldShowPriveeBanner(true);
      return;
    }

    const cached = readPriveeCache();
    if (cached) {
      setShouldShowPriveeBanner(cached.invited);
      return;
    }

    // Bail out after 2s so a slow Redis can't leave the request hanging.
    // We don't cache on abort/error so we'll retry next mount.
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 2000);

    fetch('/api/user/privee/invited-check', { signal: abortController.signal })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setShouldShowPriveeBanner(false);
          return;
        }
        const data = (await res.json()) as { invited?: boolean };
        const invited = Boolean(data?.invited);
        writePriveeCache(invited);
        setShouldShowPriveeBanner(invited);
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.name !== 'AbortError') {
          console.error('[privee] invited-check fetch failed:', error);
        }
        setShouldShowPriveeBanner(false);
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      abortController.abort();
    };
    // closeModal is referenced via closure; not in deps because the modal
    // context recreates it every render and would trigger a refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.priveeStatus, isOnExcludedPage]);

  const handlePriveeAccept = useCallback(() => {
    const priceStr = FallbackSubscriptionPrices.Privee.asFormattedString.slice(
      0,
      -3
    );
    const initFeeStr =
      FallbackSubscriptionPrices.PriveeInitiationFee.asFormattedString.slice(
        0,
        -3
      );
    const showInitFee = Boolean(
      flagValues[FLAG_KEYS.PRIVEE_CHARGE_INITIATION_FEE]
    );

    showModal(
      <AcceptInvitationModal
        variant='banner'
        priveePriceStr={priceStr}
        priveeInitFeeStr={showInitFee ? initFeeStr : null}
        onClose={() => closeModal()}
      />
    );
  }, [flagValues, showModal, closeModal]);

  const handlePriveeDismiss = useCallback(() => {
    try {
      sessionStorage.setItem(PRIVEE_INVITED_DISMISS_KEY, 'true');
    } catch {
      // Ignore
    }
    setShouldShowPriveeBanner(false);
  }, []);

  return (
    <>
      {displayAppBanner && (
        <AppBanner handleCloseBanner={handleCloseAppBanner} />
      )}
      {shouldShowPriveeBanner && (
        <PriveeInvitedBanner
          onAccept={handlePriveeAccept}
          onDismiss={handlePriveeDismiss}
        />
      )}
    </>
  );
};

export default BannerOrchestrator;
